import { Portal, Tooltip as ArkTooltip } from "@ark-ui/react";
import type { ReactElement, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { twJoin } from "tailwind-merge";

import { tooltipSurfaceClassName } from "./TooltipSurface";

const TOOLTIP_OFFSET_VALUES = {
  overlap: -3,
  default: undefined,
  spacious: 5,
} as const;

export type TooltipOffset = keyof typeof TOOLTIP_OFFSET_VALUES;

export interface TooltipProps {
  tooltip: ReactNode;
  children: ReactElement;
  offset?: TooltipOffset;
  width?: "default" | "compact";
}

export function Tooltip({ children, tooltip, offset = "default", width = "default" }: TooltipProps): ReactNode {
  if (tooltip == null || tooltip === "") {
    return children;
  }

  return (
    <TooltipContent tooltip={tooltip} offset={offset} width={width}>
      {children}
    </TooltipContent>
  );
}

function TooltipContent({ children, tooltip, offset = "default", width = "default" }: TooltipProps): ReactNode {
  const childType = children.type;
  const isInteractiveChild =
    childType === "button" ||
    childType === "a" ||
    childType === "input" ||
    childType === "label" ||
    childType === "select" ||
    childType === "textarea";

  const justPressedTab = useKeyUpPressed("Tab", 10);
  // asChild merges the child's props over the trigger's, so an id already on the
  // child (e.g. from a menu trigger) wins. Anchor the tooltip to that id instead.
  const childId = isInteractiveChild ? (children.props as { id?: string }).id : undefined;

  return (
    <ArkTooltip.Root
      ids={childId ? { trigger: childId } : undefined}
      openDelay={500}
      closeDelay={500}
      positioning={{
        placement: "top",
        offset: {
          mainAxis: TOOLTIP_OFFSET_VALUES[offset],
        },
      }}
    >
      <ArkTooltip.Trigger
        type={isInteractiveChild ? undefined : "button"}
        onFocus={(event) => {
          if (!justPressedTab) {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        asChild={isInteractiveChild}
        className={isInteractiveChild ? "" : "cursor-inherit"}
      >
        {children}
      </ArkTooltip.Trigger>
      <Portal>
        <ArkTooltip.Positioner className="z-tooltip!">
          <ArkTooltip.Content
            className={twJoin(
              "max-w-tooltip-fluid",
              tooltipSurfaceClassName,
              width === "default" ? "md:max-w-tooltip-wide" : "md:max-w-tooltip-compact",
              "repeat-1 data-[placement=bottom]:origin-top data-[placement=left]:origin-right data-[placement=right]:origin-left data-[placement=top]:origin-bottom",
              "data-[state=open]:animate-in data-[state=open]:fade-in",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out",
            )}
          >
            <ArkTooltip.Arrow className="tooltip-arrow">
              <ArkTooltip.ArrowTip />
            </ArkTooltip.Arrow>
            {tooltip}
          </ArkTooltip.Content>
        </ArkTooltip.Positioner>
      </Portal>
    </ArkTooltip.Root>
  );
}

function useKeyUpPressed(key: string, timeout: number): boolean {
  const isKeyPressedRef = useRef(false);
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === key) {
        isKeyPressedRef.current = true;

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        const newTimeoutId = setTimeout(() => {
          isKeyPressedRef.current = false;
        }, timeout);

        setTimeoutId(newTimeoutId);
      }
    };

    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keyup", handleKeyUp);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [key, timeout, timeoutId]);

  // oxlint-disable-next-line react-hooks/refs
  return isKeyPressedRef.current;
}
